import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import Modal from '@/components/ui/Modal';
import Botao from '@/components/ui/Botao';
import NumeroAnimado from '@/components/movimento/NumeroAnimado';
import { formatarCentavos } from '@/pages/loja/utils';
import type { EventoCaixa, LinhaPedido, MeioRecebimento, ProdutoCaixa } from '../types';

interface Props {
  comMaquininha: boolean;
  evento: EventoCaixa;
  linhas: LinhaPedido[];
  total: number;
  registrando: boolean;
  aoLimpar: () => void;
  aoPagar: (meio: MeioRecebimento) => void;
  aoAdicionarLinha: (p: ProdutoCaixa) => void;
  aoRemoverLinha: (p: ProdutoCaixa) => void;
}

interface BotaoReceber {
  meio: MeioRecebimento;
  rotulo: string;
  icone: string;
  cor: string;
}

const CORES: Record<string, string> = {
  dinheiro: 'bg-primary-500 hover:bg-primary-600',
  pix: 'bg-accent-500 hover:bg-accent-600',
  debito: 'bg-secondary-500 hover:bg-secondary-600',
  credito: 'bg-secondary-500 hover:bg-secondary-600',
  cartao: 'bg-secondary-500 hover:bg-secondary-600',
};

// Resumo do pedido (linhas, subtotais, total) + botões de receber.
export default function PainelPedido({
  comMaquininha,
  evento,
  linhas,
  total,
  registrando,
  aoLimpar,
  aoPagar,
  aoAdicionarLinha,
  aoRemoverLinha,
}: Props) {
  const [confirmandoLimpar, setConfirmandoLimpar] = useState(false);

  let receber: BotaoReceber[];
  if (comMaquininha) {
    // Com maquininha: Pix/Débito/Crédito sempre habilitados (é a máquina que recebe).
    receber = [
      { meio: 'pix', rotulo: 'Pix', icone: 'ri-qr-code-line', cor: CORES.pix },
      { meio: 'debito', rotulo: 'Débito', icone: 'ri-bank-card-line', cor: CORES.debito },
      { meio: 'credito', rotulo: 'Crédito', icone: 'ri-bank-card-2-line', cor: CORES.credito },
    ];
    if (evento.aceita_dinheiro) {
      receber.unshift({
        meio: 'dinheiro',
        rotulo: 'Dinheiro',
        icone: 'ri-money-dollar-circle-line',
        cor: CORES.dinheiro,
      });
    }
  } else {
    // Sem maquininha: exatamente como hoje, obedecendo as opções do evento.
    receber = [];
    if (evento.aceita_dinheiro) {
      receber.push({ meio: 'dinheiro', rotulo: 'Dinheiro', icone: 'ri-money-dollar-circle-line', cor: CORES.dinheiro });
    }
    if (evento.aceita_pix) {
      receber.push({ meio: 'pix', rotulo: 'Pix', icone: 'ri-qr-code-line', cor: CORES.pix });
    }
    if (evento.aceita_cartao) {
      receber.push({ meio: 'cartao', rotulo: 'Cartão', icone: 'ri-bank-card-line', cor: CORES.cartao });
    }
    // Nenhuma forma ligada: não deixa o operador sem saída.
    if (receber.length === 0) {
      receber = [
        { meio: 'dinheiro', rotulo: 'Dinheiro', icone: 'ri-money-dollar-circle-line', cor: CORES.dinheiro },
        { meio: 'pix', rotulo: 'Pix', icone: 'ri-qr-code-line', cor: CORES.pix },
        { meio: 'cartao', rotulo: 'Cartão', icone: 'ri-bank-card-line', cor: CORES.cartao },
      ];
    }
  }

  return (
    <div className="flex flex-col rounded-2xl border border-secondary-200 bg-white">
      <div className="flex-1 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-foreground-950">Pedido</h2>
          {linhas.length > 0 && (
            <button
              type="button"
              onClick={() => setConfirmandoLimpar(true)}
              className="cursor-pointer whitespace-nowrap text-sm font-medium text-secondary-500 transition-colors hover:text-danger-600"
            >
              Limpar
            </button>
          )}
        </div>

        {linhas.length === 0 ? (
          <p className="mt-3 text-sm text-secondary-500">Toque nos produtos para montar a venda.</p>
        ) : (
          <ul className="mt-3 max-h-[24vh] space-y-2 overflow-y-auto lg:max-h-none">
            <AnimatePresence initial={false}>
            {linhas.map((l) => (
              <motion.li
                key={l.produto.produto_id}
                className="flex items-center gap-2 overflow-hidden"
                initial={{ opacity: 0.5, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, height: 0, scale: 0.95 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-semibold text-foreground-900">
                    {l.produto.nome}
                  </p>
                  <p className="text-xs text-secondary-500">
                    {formatarCentavos(l.produto.preco_centavos)} cada
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => aoRemoverLinha(l.produto)}
                    aria-label={`Tirar um ${l.produto.nome}`}
                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-secondary-200 bg-white text-lg text-foreground-800"
                  >
                    <i className="ri-subtract-line" />
                  </button>
                  <span className="w-7 text-center text-base font-bold tabular-nums text-foreground-950">
                    {l.quantidade}
                  </span>
                  <button
                    type="button"
                    onClick={() => aoAdicionarLinha(l.produto)}
                    aria-label={`Mais um ${l.produto.nome}`}
                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-secondary-200 bg-white text-lg text-foreground-800"
                  >
                    <i className="ri-add-line" />
                  </button>
                </div>
                <span className="w-20 shrink-0 text-right text-[15px] font-bold tabular-nums text-foreground-950">
                  {formatarCentavos(l.produto.preco_centavos * l.quantidade)}
                </span>
              </motion.li>
            ))}
            </AnimatePresence>
          </ul>
        )}

        <div className="mt-4 border-t border-secondary-200 pt-3">
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-semibold text-foreground-600">TOTAL</span>
            <NumeroAnimado
              valor={formatarCentavos(total)}
              className="text-4xl font-extrabold tabular-nums text-foreground-950"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-secondary-200 px-4 pt-3">
        <span className="text-[12px] font-bold uppercase tracking-wide text-foreground-500">
          Cobrar
        </span>
      </div>
      <div
        className={`grid gap-2 px-4 pb-4 pt-2 ${
          comMaquininha ? 'grid-cols-2' : 'grid-cols-3'
        }`}
      >
        {receber.map((m) => (
          <button
            key={m.meio}
            type="button"
            disabled={total === 0 || registrando}
            onClick={() => aoPagar(m.meio)}
            className={`flex items-center justify-center gap-1.5 whitespace-nowrap rounded-xl font-extrabold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
              comMaquininha ? 'h-16 text-base' : 'h-14 text-sm'
            } ${m.cor}`}
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className={m.icone} />
            </span>
            {registrando ? '…' : m.rotulo}
          </button>
        ))}
      </div>

      <Modal
        aberto={confirmandoLimpar}
        titulo="Limpar tudo?"
        aoFechar={() => setConfirmandoLimpar(false)}
      >
        <p className="text-[15px] text-secondary-600">
          As fichas montadas saem do pedido. Nada foi cobrado ainda.
        </p>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Botao variante="secundario" onClick={() => setConfirmandoLimpar(false)}>
            Não
          </Botao>
          <Botao
            variante="perigo"
            icone="ri-delete-bin-line"
            onClick={() => {
              aoLimpar();
              setConfirmandoLimpar(false);
            }}
          >
            Sim, limpar
          </Botao>
        </div>
      </Modal>
    </div>
  );
}